import { FunctionComponent, useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import styles from './SelectComponent.module.scss';

export interface SelectOption {
    id: string | number;
    name: string;
    [key: string]: any;
}

export interface SelectComponentProps {
    id?: string;
    label?: string | JSX.Element;
    name?: string;
    selectedOption?: SelectOption;
    options: SelectOption[];
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    borderStyle?: string;
    borderExpandedStyle?: string;
    required?: boolean;
    onSelect: (option: SelectOption) => void;
    getOptionLabel?: (option: SelectOption) => string;
    getOptionValue?: (option: SelectOption) => string | number;
    getOptionDisabled?: (option: SelectOption) => boolean;
    getOptionDisabledText?: (option: SelectOption) => string | undefined;
}

const SelectComponent: FunctionComponent<SelectComponentProps> = props => {
    const selectRef = useRef<HTMLDivElement>(null);

    const [isCollapsed, setIsCollapsed] = useState(true);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setIsCollapsed(true);
            }
        };

        if (!isCollapsed) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isCollapsed]);

    const clickCollapseHandler = () => {
        if (!props.disabled) {
            setIsCollapsed(prev => !prev);
        }
    };

    const changeHandler = (selectedOption: SelectOption) => {
        setIsCollapsed(true);
        props.onSelect(selectedOption);
    };

    const getSelectedOption = () => {
        const getValue = props.getOptionValue || ((option: SelectOption) => option.id);
        const selectedValue = props.selectedOption ? getValue(props.selectedOption) : null;
        return props.options.find(option => getValue(option) === selectedValue);
    };

    const getOptionLabel = (option: SelectOption) => {
        return props.getOptionLabel ? props.getOptionLabel(option) : option.name;
    };

    const isOptionDisabled = (option: SelectOption) => {
        return props.getOptionDisabled ? props.getOptionDisabled(option) : false;
    };

    const getOptionDisabledText = (option: SelectOption) => {
        return props.getOptionDisabledText ? props.getOptionDisabledText(option) : undefined;
    };

    const header = (
        <div className={styles.headerContainer}>
            <div className={styles.textContainer}>
                {props.selectedOption ? (
                    <div className={clsx(styles.itemContent, styles.itemContentSelected, styles.text)}>
                        <p className={styles.text}>{getSelectedOption()?.name}</p>
                    </div>
                ) : (
                    <p className={clsx(styles.placeholder, styles.text)}>{props.placeholder}</p>
                )}
            </div>
            <div className={styles.chevronContainer}>
                <FontAwesomeIcon
                    icon={isCollapsed ? faChevronDown : faChevronUp}
                    color={isCollapsed ? '#7A7A7A' : '#222222'}
                    className={styles.chevron}
                />
            </div>
        </div>
    );

    const list = (
        <div className={styles.listContainer}>
            {props.options.map((option, index) => {
                const disabled = isOptionDisabled(option);
                const disabledText = getOptionDisabledText(option);

                return (
                    <div
                        className={clsx(styles.itemContainer, {
                            [styles.itemContainerDisabled]: disabled
                        })}
                        key={option.id}
                        id={`select_option_${index}`}
                        onClick={() => !disabled && changeHandler(option)}
                    >
                        <div
                            className={clsx(styles.itemContent, {
                                [styles.itemContentDisabled]: disabled
                            })}
                        >
                            <div className={styles.optionNameContainer}>
                                <p
                                    className={clsx(styles.item, styles.sectionOption, {
                                        [styles.itemDisabled]: disabled
                                    })}
                                >
                                    {getOptionLabel(option)}
                                </p>
                            </div>
                            {disabled && disabledText && (
                                <div className={styles.disabledTextContainer}>
                                    <p className={styles.disabledText}>{disabledText}</p>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const collapsed = (
        <div
            className={clsx(styles.border, styles.collapsedContainer, props.borderStyle, {
                [styles.optionNotSelected]: !props.selectedOption
            })}
            onClick={clickCollapseHandler}
        >
            {header}
        </div>
    );

    const expanded = (
        <div className={clsx(styles.border, styles.expandedContainer, props.borderExpandedStyle)}>
            <div onClick={clickCollapseHandler}>{header}</div>
            {list}
        </div>
    );

    return (
        <div
            ref={selectRef}
            className={clsx(styles.container, props.className)}
        >
            <div className={styles.labelContainer}>
                {props.label && (
                    <label className={styles.label}>
                        {props.label}
                        {props.required && <span className={styles.required}>*</span>}
                    </label>
                )}
            </div>
            <div
                className={styles.dropdownContainer}
                id={props.id || 'generic_select_dropdown'}
            >
                {isCollapsed ? collapsed : expanded}
            </div>
        </div>
    );
};

export default SelectComponent;
